import { ReactNode } from 'react';
import { Dayjs } from 'dayjs';
import { Control, FieldErrors } from 'react-hook-form';
import { TimeView } from '@mui/x-date-pickers/models';
import { TimeRange, TimeRangeEdge } from '@models/time-range-picker.model.ts';
import { PlaylistScreenOption } from '@modules/digital-signage/model/playlists.model.ts';
import { StatusLozengeToneEnum } from '@modules/digital-signage/model/group-settings.model.ts';
import {
  DeviceGroupScheduleModel,
  DeviceGroupScheduleReqModel,
  WeekDayEnum
} from '@models/digital-signage/device-group-schedules.model.ts';

export enum ScheduleStatusEnum {
  Active = 'active',
  Live = 'live',
  Upcoming = 'upcoming',
  Ended = 'ended',
  Draft = 'draft'
}

export enum ScheduleTimeStatusEnum {
  Scheduled = 'scheduled',
  Live = 'live',
  DoneToday = 'done_today',
  Ended = 'ended'
}

export interface ScheduleTimeline {
  status: ScheduleTimeStatusEnum;
  liveUntil: number | null;
  nextDate: string | null;
  lastDate: string | null;
}

export interface ScheduleTimeStatusConfig {
  label: string;
  tone: StatusLozengeToneEnum | null;
  cardTime: string;
}

export enum ScheduleDayAppearanceEnum {
  Free = 'free',
  Idle = 'idle',
  Scheduled = 'scheduled',
  Live = 'live',
  DoneToday = 'done_today',
  Ended = 'ended',
  Conflict = 'conflict'
}

export interface ScheduleDayAppearanceConfig {
  chip: string;
  chipTitle: string;
  chipSubtitle: string;
  cardDay: string;
  legend: ScheduleLegendPaletteEnum;
}

export enum ScheduleLegendPaletteEnum {
  Default = 'default',
  Muted = 'muted'
}

export interface ScheduleDayAppearanceParams {
  selected: boolean;
  status?: ScheduleTimeStatusEnum;
  hasConflict?: boolean;
  isOutOfRange?: boolean;
  isFull?: boolean;
  disabled?: boolean;
}

export enum ConflictSourceEnum {
  /** A schedule already saved for the group */
  Existing = 'existing',
  /** Another rule / time range of the schedule being edited */
  Draft = 'draft'
}

export enum ScheduleDateErrorEnum {
  EndBeforeStart = 'endBeforeStart',
  StartInPast = 'startInPast',
  EndInPast = 'endInPast'
}

export enum ScheduleLegendEnum {
  Free = 'free',
  Booked = 'booked',
  ThisSchedule = 'this_schedule',
  Conflict = 'conflict'
}

export enum ScheduleDayPresetEnum {
  Weekdays = 'weekdays',
  Weekend = 'weekend',
  All = 'all'
}

/** Minutes from 00:00, half-open [start, end), end = 1440 is the end of day */
export interface MinutesRange {
  start: number;
  end: number;
}

export type ScheduleTiming = Pick<
  DeviceGroupScheduleModel,
  'startDate' | 'endDate' | 'schedules'
>;

/** 'YYYY-MM-DD' dates, null means an open boundary */
export interface ScheduleDateRange {
  start: string | null;
  end: string | null;
}

export interface OccupiedSlot extends MinutesRange {
  day: WeekDayEnum;
  source: ConflictSourceEnum;
  /** Existing schedule name */
  ownerName?: string;
  ownerId?: string;
  /** Draft rule / range position */
  ruleIndex?: number;
  rangeIndex?: number;
}

export interface ScheduleOccupancy {
  dateError: ScheduleDateErrorEnum | null;
  /** Week days that really occur in the schedule period */
  allowedDays: WeekDayEnum[];
  endedDays: WeekDayEnum[];
  period: ScheduleDateRange;
  now: Dayjs;
  existing: OccupiedSlot[];
  draft: OccupiedSlot[];
}

export interface ScheduleRangeValidation {
  isIncomplete: boolean;
  isInvalid: boolean;
  isPast: boolean;
  conflicts: OccupiedSlot[];
}

export interface ScheduleRuleValidation {
  hasNoDays: boolean;
  hasOnlyEndedDays: boolean;
  daysOutOfRange: WeekDayEnum[];
  ranges: ScheduleRangeValidation[];
  conflicts: OccupiedSlot[];
  conflictDays: WeekDayEnum[];
  /** Range the suggestions are meant for */
  suggestionRangeIndex: number;
  suggestions: MinutesRange[];
  isBlocked: boolean;
}

export interface ScheduleValidationResult {
  isValid: boolean;
  blockingCount: number;
  dateError: ScheduleDateErrorEnum | null;
  rules: ScheduleRuleValidation[];
}

export interface DayAvailability {
  freeMinutes: number;
  isFull: boolean;
  segments: MinutesRange[];
}

export interface ScheduleRuleFormValue {
  days: WeekDayEnum[];
  timeRanges: TimeRange[];
}

export interface ScheduleFormValues {
  name: string;
  playlist: PlaylistScreenOption | null;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  rules: ScheduleRuleFormValue[];
}

export interface AccordionContentTransitionProps {
  children?: ReactNode;
}

export interface PlaylistScheduleProps {
  groupId?: string;
}

export interface ScheduleCardProps {
  schedule: DeviceGroupScheduleModel;
  color: string;
  savedSlotKeys: Set<string>;
  onEdit: (schedule: DeviceGroupScheduleModel) => void;
  onDelete: (schedule: DeviceGroupScheduleModel) => void;
}

export interface ScheduleDialogFormProps {
  selectedItem: DeviceGroupScheduleModel | null;
  onSave: (body: DeviceGroupScheduleReqModel) => void;
  onCancel: () => void;
}

export interface ScheduleDatePickerProps {
  name: keyof Pick<ScheduleFormValues, 'startDate' | 'endDate'>;
  label: string;
  control: Control<ScheduleFormValues>;
  errors: FieldErrors<ScheduleFormValues>;
  dateError: ScheduleDateErrorEnum | null;
  minDate?: Dayjs;
  maxDate?: Dayjs;
}

export interface ScheduleRuleCardProps {
  index: number;
  rule: ScheduleRuleFormValue;
  occupancy: ScheduleOccupancy;
  validation?: ScheduleRuleValidation;
  canDelete: boolean;
  showIndex: boolean;
  disabled?: boolean;
  savedSlotKeys: Set<string>;
  onDaysChange: (days: WeekDayEnum[]) => void;
  onRangesChange: (ranges: TimeRange[]) => void;
  onDelete: () => void;
}

export interface ScheduleTimeDisabledParams {
  value: Dayjs;
  view: TimeView;
  edge: TimeRangeEdge;
  rangeIndex: number;
  ruleIndex: number;
  rule: ScheduleRuleFormValue;
  occupancy: ScheduleOccupancy;
}

export interface WeekDayChipProps {
  day: WeekDayEnum;
  selected: boolean;
  hasConflict: boolean;
  isOutOfRange: boolean;
  status?: ScheduleTimeline;
  disabled?: boolean;
  availability: DayAvailability;
  ownRanges: MinutesRange[];
  onToggle: (day: WeekDayEnum) => void;
}

export interface ScheduleStatusBadgeProps {
  status: ScheduleTimeStatusEnum;
  className?: string;
}
